package link.erpango.quip.core.interactor

import io.ktor.client.plugins.ResponseException
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.noteimport.BlockReason
import link.erpango.quip.domain.noteimport.ImportPlan
import link.erpango.quip.domain.noteimport.ItemStatus
import link.erpango.quip.domain.noteimport.MaintenanceDraft
import link.erpango.quip.domain.noteimport.SparePartDraft
import link.erpango.quip.domain.noteimport.VehicleDraft
import link.erpango.quip.domain.noteimport.VehicleRef
import link.erpango.quip.domain.noteimport.toDMaintenance
import link.erpango.quip.domain.noteimport.toDMySparePart
import link.erpango.quip.domain.noteimport.toDVehicle
import link.erpango.quip.domain.services.SignatureService
import link.erpango.quip.domain.services.signNewMaintenance
import link.erpango.quip.domain.usecases.GarageErrors
import link.erpango.quip.domain.usecases.IGarageUseCase
import link.erpango.quip.domain.usecases.IMySparePartsUseCase

/**
 * Creates what the user confirmed in the bulk review, one item at a time and in dependency
 * order, through the same use cases as the forms:
 *
 * 1. Vehicles (signed like the form). A vehicle already in the garage is only attached.
 * 2. Maintenances, per vehicle and oldest first, chain-signed on top of the vehicle's existing
 *    maintenances exactly like [link.erpango.quip.features.maintenances.MaintenancesViewModel].
 * 3. Spare parts into "Mis repuestos", linked to their vehicle's VIN.
 *
 * A failed vehicle blocks its maintenances and parts. Items already settled in [previous]
 * (created or already existing) are skipped, so a retry never creates duplicates.
 */
class NoteImportBulkCreator(
    private val garage: IGarageUseCase,
    private val mySpareParts: IMySparePartsUseCase,
) {

    suspend fun execute(
        plan: ImportPlan,
        previous: Map<String, ItemStatus> = emptyMap(),
        onProgress: (id: String, status: ItemStatus) -> Unit = { _, _ -> },
    ): Map<String, ItemStatus> {
        val statuses = previous.toMutableMap()
        fun report(id: String, status: ItemStatus) {
            statuses[id] = status
            onProgress(id, status)
        }

        // 1. Vehicles -> the VIN every draft vehicle ended up with.
        val vinByDraftId = mutableMapOf<String, String>()
        for (vehicle in plan.vehicles) {
            when (val done = statuses[vehicle.id]) {
                is ItemStatus.Created -> done.vin?.let { vinByDraftId[vehicle.id] = it }
                is ItemStatus.AlreadyExisted -> done.vin?.let { vinByDraftId[vehicle.id] = it }
                else -> {
                    report(vehicle.id, ItemStatus.InProgress)
                    val status = createVehicle(vehicle)
                    report(vehicle.id, status)
                    when (status) {
                        is ItemStatus.Created -> status.vin?.let { vinByDraftId[vehicle.id] = it }
                        is ItemStatus.AlreadyExisted -> status.vin?.let { vinByDraftId[vehicle.id] = it }
                        else -> Unit
                    }
                }
            }
        }

        fun vinFor(ref: VehicleRef): String? = when (ref) {
            is VehicleRef.Garage -> ref.vin
            is VehicleRef.Draft -> vinByDraftId[ref.draftId]
            VehicleRef.Unassigned -> null
        }

        // 2. Maintenances, grouped per vehicle and in date order so the signature chain follows time.
        val pendingMaintenances = plan.maintenances.filterNot { statuses[it.id]?.isSettled == true }
        for (maintenance in pendingMaintenances.filter { it.vehicle == VehicleRef.Unassigned }) {
            report(maintenance.id, ItemStatus.Blocked(BlockReason.NO_VEHICLE))
        }
        val byVin = pendingMaintenances
            .filter { it.vehicle != VehicleRef.Unassigned }
            .groupBy { vinFor(it.vehicle) }
        for ((vin, maintenances) in byVin) {
            if (vin == null) {
                maintenances.forEach { report(it.id, ItemStatus.Blocked(BlockReason.VEHICLE_NOT_CREATED)) }
                continue
            }
            val chain = existingMaintenances(vin).toMutableList()
            for (maintenance in maintenances.sortedBy { it.date?.value ?: Long.MAX_VALUE }) {
                report(maintenance.id, ItemStatus.InProgress)
                val status = createMaintenance(maintenance, vin, chain)
                report(maintenance.id, status)
            }
        }

        // 3. Spare parts ("Mis repuestos"): the vehicle is optional, but a failed one blocks.
        for (part in plan.spareParts.filterNot { statuses[it.id]?.isSettled == true }) {
            val vin = vinFor(part.vehicle)
            if (part.vehicle != VehicleRef.Unassigned && vin == null) {
                report(part.id, ItemStatus.Blocked(BlockReason.VEHICLE_NOT_CREATED))
                continue
            }
            report(part.id, ItemStatus.InProgress)
            report(part.id, createSparePart(part, listOfNotNull(vin)))
        }
        return statuses
    }

    private suspend fun createVehicle(draft: VehicleDraft): ItemStatus {
        draft.garageMatchVin?.let { return ItemStatus.AlreadyExisted(it) }
        val vehicle = draft.toDVehicle() ?: return ItemStatus.Blocked(BlockReason.INCOMPLETE)
        val signed = vehicle.copy(signature = SignatureService.signVehicle(vehicle))
        return when (val result = garage.createVehicle(signed)) {
            is DResponse.Success -> ItemStatus.Created(result.data.vin)
            is DResponse.Error ->
                if (result.error == GarageErrors.DUPLICATE_LOCAL_VIN) ItemStatus.AlreadyExisted(vehicle.vin)
                else ItemStatus.Failed(result.error)
            is DResponse.Complete -> ItemStatus.Created(vehicle.vin)
        }
    }

    /** The vehicle's maintenances, refreshed from the server when possible so the chain is current. */
    private suspend fun existingMaintenances(vin: String): List<DMaintenance> {
        val remote = garage.fetchMaintenancesByVehicle(vin)
        if (remote is DResponse.Success) garage.syncMaintenances(remote.data)
        return garage.getMaintenancesByVehicleWithSpareParts(vin)
    }

    private suspend fun createMaintenance(
        draft: MaintenanceDraft,
        vin: String,
        chain: MutableList<DMaintenance>,
    ): ItemStatus {
        val maintenance = draft.toDMaintenance(vin) ?: return ItemStatus.Blocked(BlockReason.INCOMPLETE)
        val duplicate = chain.any {
            it.date == maintenance.date && it.kilometers == maintenance.kilometers &&
                it.description.equals(maintenance.description, ignoreCase = true)
        }
        if (duplicate) return ItemStatus.AlreadyExisted()
        val signed = signNewMaintenance(maintenance, chain)
        return when (val result = garage.addMaintenance(signed)) {
            is DResponse.Success -> {
                chain += result.data.copy(signature = result.data.signature ?: signed.signature)
                ItemStatus.Created()
            }
            is DResponse.Error -> ItemStatus.Failed(result.error)
            is DResponse.Complete -> {
                chain += signed
                ItemStatus.Created()
            }
        }
    }

    private suspend fun createSparePart(draft: SparePartDraft, vins: List<String>): ItemStatus {
        val part = draft.toDMySparePart(vins) ?: return ItemStatus.Blocked(BlockReason.INCOMPLETE)
        return when (val result = mySpareParts.createMySparePart(part)) {
            is DResponse.Success, is DResponse.Complete -> ItemStatus.Created()
            is DResponse.Error ->
                // 409: the user already has this exact part (same category, type, brand, model).
                if ((result.throwable as? ResponseException)?.response?.status?.value == 409) ItemStatus.AlreadyExisted()
                else ItemStatus.Failed(result.error)
        }
    }
}
